import React from 'react'
import { Box, Typography, Button, Stack } from '@mui/material'
import { ExternalUrlNodeEntry } from './types'
import { Table } from 'shared/ui/Table/Table'
import { Column } from 'shared/ui/Table/types'
import { openNode, openNodeOnTKatBuilder } from '../utility/TreeNavigation'
import { NodeIdMap } from '../version_mapping/utility'
import {
  createFieldPathColumn,
  createUrlsFoundColumn,
  createTypeColumn,
} from './sharedTableColumns'

/**
 * Displays external URLs found in content tree nodes in a table format.
 * @param externalUrls - Array of node entries containing external URL matches
 * @param title - The title to display for the table (e.g., "External Node URLs", "Invalid Node URLs")
 * @param idMap - Optional node ID map for navigation links
 * @returns Table component showing node details and extracted URLs, or null if empty
 */
export function ExternalUrlsNodesTable({
  externalUrls,
  title = 'External Node URLs',
  idMap,
}: {
  externalUrls: ExternalUrlNodeEntry[]
  title?: string
  idMap?: NodeIdMap
}) {
  if (externalUrls.length === 0) {
    return null
  }

  const hasNavigation = !!idMap

  const columns: Column<ExternalUrlNodeEntry>[] = [
    {
      title: 'Node ID',
      rowContent: ({ node_id }) => {
        if (!hasNavigation || !idMap) {
          return (
            <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
              {node_id}
            </Typography>
          )
        }

        const nodeInfo = idMap[node_id]
        if (!nodeInfo) {
          return (
            <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
              {node_id}
            </Typography>
          )
        }

        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              onClick={() => openNode(nodeInfo.node, idMap)}
            >
              Kiddom
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => openNodeOnTKatBuilder(nodeInfo.node)}
            >
              TKat
            </Button>
          </Stack>
        )
      },
    },
    {
      title: 'Node Title',
      rowContent: ({ node_title }) => (
        <Typography variant="body2">{node_title}</Typography>
      ),
    },
    createTypeColumn<ExternalUrlNodeEntry>(
      'Node Type',
      (item) => item.node_type,
    ),
    createFieldPathColumn<ExternalUrlNodeEntry>(),
    createUrlsFoundColumn<ExternalUrlNodeEntry>(),
  ]

  return (
    <Box padding="10px">
      <Table
        title={`${title} (${externalUrls.length} node${externalUrls.length !== 1 ? 's' : ''})`}
        items={externalUrls}
        columns={columns}
      />
    </Box>
  )
}
